import React from "react";
import { useFormikContext } from "formik";
import { Col, Row } from "react-bootstrap";
import { AddressField, type AddressFieldName } from "./AddressField";
import { PostcodeLookup, type PostcodeSelectorLayout } from "./PostcodeLookup";
import {
  DEFAULT_ADDRESS_FIELD_LAYOUT_CONFIG,
  DEFAULT_ADDRESS_REQUIRED_FIELDS,
  type AddressFieldLayoutConfig,
  type AddressLayoutVariant,
} from "./addressFormSectionConfig";

interface AddressFormSectionProps {
  layoutVariant: AddressLayoutVariant;
  postcodeLayout: PostcodeSelectorLayout;
  requiredFields?: AddressFieldName[];
  fieldPrefix?: string;
  className?: string;
  fieldLayoutConfig?: Partial<AddressFieldLayoutConfig>;
}

interface FormikAddressShape {
  values: any;
  errors: any;
  touched: any;
  handleChange: any;
  setFieldValue: any;
  setFieldError: any;
  setFieldTouched: any;
  setStatus: any;
}

const ADDRESS_FIELD_LABELS: Record<AddressFieldName, string> = {
  addressLine1: "Address line 1",
  addressLine2: "Address line 2",
  addressLine3: "Address line 3",
  town: "Town",
  county: "County",
  postcode: "Postcode",
  country: "Country",
};

function mergeFieldLayoutConfig(overrides?: Partial<AddressFieldLayoutConfig>): AddressFieldLayoutConfig {
  return {
    pairedRows: overrides?.pairedRows ?? DEFAULT_ADDRESS_FIELD_LAYOUT_CONFIG.pairedRows,
    stacked: overrides?.stacked ?? DEFAULT_ADDRESS_FIELD_LAYOUT_CONFIG.stacked,
  };
}

export const AddressFormSection: React.FC<AddressFormSectionProps> = ({
  layoutVariant,
  postcodeLayout,
  requiredFields = DEFAULT_ADDRESS_REQUIRED_FIELDS,
  fieldPrefix,
  className = "",
  fieldLayoutConfig,
}) => {
  const { values, errors, touched, handleChange, setFieldValue, setFieldError, setFieldTouched, setStatus } =
    useFormikContext<FormikAddressShape>();
  const layoutConfig = mergeFieldLayoutConfig(fieldLayoutConfig);
  const sectionClasses = `address-form-section address-form-section-gap ${className}`.trim();

  const renderField = (fieldName: AddressFieldName) => (
    <AddressField
      key={fieldName}
      fieldName={fieldName}
      label={ADDRESS_FIELD_LABELS[fieldName]}
      values={values}
      errors={errors}
      touched={touched}
      handleChange={handleChange}
      fieldPrefix={fieldPrefix}
      required={requiredFields.includes(fieldName)}
      className="mb-0"
    />
  );

  return (
    <div className={sectionClasses}>
      <PostcodeLookup
        values={values}
        errors={errors}
        touched={touched}
        handleChange={handleChange}
        setFieldValue={setFieldValue}
        setFieldError={setFieldError}
        setFieldTouched={setFieldTouched}
        setStatus={setStatus}
        fieldPrefix={fieldPrefix}
        selectorLayout={postcodeLayout}
        required={requiredFields.includes("postcode")}
        className="mb-0"
      />

      {layoutVariant === "pairedRows"
        ? layoutConfig.pairedRows.map((rowFields, index) => (
            <Row className="g-3 address-form-section-row" key={`address-row-${index}`}>
              {rowFields.map((fieldName) => (
                <Col md={6} key={fieldName}>
                  {renderField(fieldName)}
                </Col>
              ))}
            </Row>
          ))
        : layoutConfig.stacked.map((fieldName) => renderField(fieldName))}
    </div>
  );
};
